import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MainLayoutComponent } from '../../../_layouts/main-layout/main-layout.component';
import { LandscaperService } from '../../../_services/landscaper.service';
import { AlertService } from '../../../_services/alert.service';
import { Landscaper } from '../../../_interfaces/landscaper';
import { CreateEditLandscaperComponent } from '../create-edit-landscaper/create-edit-landscaper.component';
import { RecordNavComponent, confirmLeave } from '../../../../Shared/_components/record-nav/record-nav.component';
import type { LandscaperListState } from '../landscapers.component';

// A landscaper on its own page: /landscapers/create, /landscapers/edit/:id.
// The form is the same component the dialog uses, in page mode (with a live
// map of the pin). Previous / Next follow the Landscapers list.
@Component({
    selector: 'app-landscaper-edit',
    imports: [CommonModule, MainLayoutComponent, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, CreateEditLandscaperComponent, RecordNavComponent],
    templateUrl: './landscaper-edit.component.html'
})
export class LandscaperEditComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private landscaperService = inject(LandscaperService);
  private alertService = inject(AlertService);

  @ViewChild(CreateEditLandscaperComponent) form?: CreateEditLandscaperComponent;

  storeId = 0;
  listState: LandscaperListState = {};
  loading = true;
  notFound = false;
  landscaper: Landscaper | null = null;
  formData: { type: 'ADD' | 'EDIT'; landscaper?: any } | null = null;
  approving = false;

  private pages = new Map<number, Landscaper[]>();
  private navPage = 0;
  private pageSize = 10;
  navTotal = 0;

  get isEdit(): boolean {
    return this.storeId > 0;
  }

  ngOnInit(): void {
    this.storeId = Number(this.route.snapshot.paramMap.get('id')) || 0;
    const qp = this.route.snapshot.queryParamMap;
    for (const k of ['q', 'status', 'sort', 'page', 'size'] as const) {
      const v = qp.get(k);
      if (v !== null) this.listState[k] = v;
    }
    this.navPage = Number(this.listState.page) || 0;
    this.pageSize = Number(this.listState.size) || 10;

    if (!this.isEdit) {
      this.formData = { type: 'ADD' };
      this.loading = false;
      return;
    }
    this.loadLandscaper();
    this.loadNavPage(this.navPage, true);
  }

  private loadLandscaper(): void {
    this.landscaperService.getLandscapers({ pageIndex: 0, pageSize: 1, selectedStatus: 'all', storeId: this.storeId } as any).subscribe({
      next: (r: any) => {
        this.landscaper = r?.stores?.[0] ?? null;
        this.notFound = !this.landscaper;
        if (this.landscaper) this.formData = { type: 'EDIT', landscaper: this.landscaper };
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.notFound = true;
      },
    });
  }

  // ---- previous / next ----
  private loadNavPage(page: number, findNeighbours = false): void {
    if (page < 0 || this.pages.has(page)) return;
    const s = this.listState;
    this.landscaperService
      .getLandscapers({ pageIndex: page, pageSize: this.pageSize, selectedStatus: s.status || 'all', keyword: s.q || '', sort: s.sort || 'default' })
      .subscribe({
        next: (r: any) => {
          const rows: Landscaper[] = r?.stores ?? [];
          this.pages.set(page, rows);
          this.navTotal = r?.count ?? 0;
          if (!findNeighbours) return;
          const i = rows.findIndex((l) => l.storeId === this.storeId);
          if (i === 0) this.loadNavPage(page - 1);
          if (i === rows.length - 1 && (page + 1) * this.pageSize < this.navTotal) this.loadNavPage(page + 1);
        },
        error: () => {},
      });
  }

  private get navRows(): Landscaper[] {
    return this.pages.get(this.navPage) ?? [];
  }

  private get navIndex(): number {
    return this.navRows.findIndex((l) => l.storeId === this.storeId);
  }

  get position(): number {
    return this.navIndex >= 0 ? this.navPage * this.pageSize + this.navIndex : -1;
  }

  get prev(): { l: Landscaper; page: number } | null {
    const i = this.navIndex;
    if (i < 0) return null;
    if (i > 0) return { l: this.navRows[i - 1], page: this.navPage };
    const before = this.pages.get(this.navPage - 1);
    return before?.length ? { l: before[before.length - 1], page: this.navPage - 1 } : null;
  }

  get next(): { l: Landscaper; page: number } | null {
    const i = this.navIndex;
    if (i < 0) return null;
    if (i < this.navRows.length - 1) return { l: this.navRows[i + 1], page: this.navPage };
    const after = this.pages.get(this.navPage + 1);
    return after?.length ? { l: after[0], page: this.navPage + 1 } : null;
  }

  private get dirty(): boolean {
    return !!this.form?.hasChanges;
  }

  goTo(target: { l: Landscaper; page: number } | null): void {
    if (!target) return;
    confirmLeave(this.dialog, this.dirty, this.landscaper?.storeName ?? '', target.l.storeName).subscribe((ok) => {
      if (!ok) return;
      this.router.navigate(['/landscapers/edit', target.l.storeId], { queryParams: { ...this.listState, page: target.page ? String(target.page) : undefined } });
    });
  }

  backToList(force = false): void {
    const go = () => this.router.navigate(['/landscapers'], { queryParams: { ...this.listState, page: this.navPage ? String(this.navPage) : undefined } });
    if (force) return void go();
    confirmLeave(this.dialog, this.dirty, this.landscaper?.storeName ?? 'this landscaper', 'the list').subscribe((ok) => ok && go());
  }

  onDone(result: { status: boolean }): void {
    if (!result.status) return this.backToList();
    if (this.isEdit) this.loadLandscaper(); // stay for Previous / Next, with the saved details
    else this.backToList(true);
  }

  approve(): void {
    const l = this.landscaper;
    if (!l || this.approving) return;
    this.approving = true;
    const done = () => {
      this.approving = false;
      this.alertService.alert('Landscaper approved - it is now on the website', 'success');
      this.loadLandscaper();
    };
    this.landscaperService.approveLandscraper(l).subscribe({
      next: done,
      error: (error) => {
        if (LandscaperService.succeeded(error)) return done();
        this.approving = false;
        this.alertService.alert(error.error?.message || 'Could not approve', 'error');
      },
    });
  }

  initials(l: Landscaper | null): string {
    const words = (l?.storeName ?? '').trim().split(/\s+/).filter(Boolean);
    return ((words[0]?.charAt(0) ?? '') + (words[1]?.charAt(0) ?? '')).toUpperCase() || '?';
  }
}
